import { Badge, Box, Flex, Group, Paper, Text, Title, Transition } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { openapi } from "../../openapi";
import { useAppSelector } from "../app/hooks";
import { useProjectId } from "../hooks/useProjectId";
import { RoomSideBar } from "../RoomSideBar/RoomSideBar";
import { DrofusEndringerFilterBar } from "./DrofusEndringerFilterBar";
import { DrofusEndringerGroupedByRoomTable } from "./DrofusEndringerGroupedByRoomTable";
import { DrofusEndringerTable } from "./DrofusEndringerTable";
import {
  selectDrofusEndringerApiArg,
  selectDrofusEndringerRoomGroupsApiArg,
  selectDrofusEndringerViewMode,
} from "./drofusEndringerSlice";

const SIDEBAR_WIDTH = 600;

export const DrofusEndringerPage: React.FC = () => {
  const projectNo = useProjectId();
  const apiArg = useAppSelector(selectDrofusEndringerApiArg);
  const roomGroupsApiArg = useAppSelector(selectDrofusEndringerRoomGroupsApiArg);
  const viewMode = useAppSelector(selectDrofusEndringerViewMode);
  const selectedSpaceNumber = useAppSelector((state) => state.design.selectedSpaceNumber);
  const copySpaceModeActive = useAppSelector((state) => state.design.copySpaceModeActive);
  const roomPanelOpen = Boolean(selectedSpaceNumber) && !copySpaceModeActive;
  const { isFetching: changesFetching, isError: changesError } =
    openapi.usePostMultiplacerDrofusInterfaceProjectChangeEventsQuery(
      viewMode === "changes" && apiArg ? apiArg : skipToken
    );
  const { isFetching: roomGroupsFetching, isError: roomGroupsError } =
    openapi.usePostMultiplacerDrofusInterfaceProjectChangeEventsByRoomQuery(
      viewMode === "rooms" && roomGroupsApiArg ? roomGroupsApiArg : skipToken
    );
  const isFetching = viewMode === "rooms" ? roomGroupsFetching : changesFetching;
  const isError = viewMode === "rooms" ? roomGroupsError : changesError;

  return (
    <Flex direction="column" h="100%" w="100%" p="md" gap="md" bg="gray.0">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Box>
          <Group gap="xs" align="center">
            <Title order={3}>Endringer</Title>
            <Badge variant="light" color="blue" radius="sm">
              dRofus
            </Badge>
            {isFetching && (
              <Badge variant="light" color="blue" radius="sm">
                Laster
              </Badge>
            )}
            {isError && (
              <Badge variant="light" color="red" radius="sm">
                Feil
              </Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed">
            Prosjekt {projectNo} · endringshistorikk på tvers av alle rom
          </Text>
        </Box>
      </Group>

      <Flex gap="md" h="100%" mih={0}>
        <DrofusEndringerFilterBar />
        <Paper withBorder radius="sm" p="sm" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {viewMode === "rooms" ? <DrofusEndringerGroupedByRoomTable /> : <DrofusEndringerTable />}
        </Paper>
        <Transition mounted={roomPanelOpen} transition="slide-left" duration={250} timingFunction="ease">
          {(styles) => (
            <Box
              w={SIDEBAR_WIDTH}
              h="100%"
              style={{
                ...styles,
                flexShrink: 0,
                borderLeft: "1px solid var(--mantine-color-gray-3)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "white",
                overflowY: "auto",
                minHeight: 0,
              }}
            >
              <RoomSideBar tabMode="all" />
            </Box>
          )}
        </Transition>
      </Flex>
    </Flex>
  );
};
